import * as THREE from 'three';
import { createArrowDebugUI } from './arrow-debug-ui.js';

// Configuration for arrow animations
const DEFAULT_CONFIG = {
   distance: 0.7, // How far down the arrow moves
   speed: 0.2, // How fast the arrow moves
   loopDuration: 1.4, // Duration between loop starts
   arrowheadSize: 0.1, // Size of the arrowhead
   strokeWidth: 0.02, // Width of the arrow line
   arrowLength: 0.2, // Length of the arrow shaft
   numArrows: 1, // Number of arrows per location
   arrowColor: 0xff0000, // Red
   enabled: true, // Show/hide arrows
};

const WÄRMELEITUNG_KEY = '05_Wärmeleitfähigkeit';

export function createArrowController(rootScene, world, options = {}) {
   const { debugUIEnabled = false, experience = null } = options;
   const group = new THREE.Group();
   group.name = 'ArrowGroup';
   group.visible = false; // Start invisible, only show in Wärmeleitfähigkeit state
   rootScene.add(group);

   const config = { ...DEFAULT_CONFIG };
   const arrows = [];
   let debugUI = null;
   let destroyed = false;
   let shouldDisplay = false;

   /**
    * Update visibility based on current experience state
    */
   function updateVisibilityFromState() {
      if (!experience) {
         group.visible = false;
         arrows.forEach((arrow) => (arrow.group.visible = false));
         return;
      }
      const activeKey = experience.getActiveKey?.();
      const isInWärmeleitung = activeKey === WÄRMELEITUNG_KEY;
      shouldDisplay = isInWärmeleitung;

      // ONLY show if we're in Wärmeleitfähigkeit state AND enabled
      // This is the only way arrows can be visible
      const shouldBeVisible = isInWärmeleitung && config.enabled;
      group.visible = shouldBeVisible;

      // Also set individual arrow visibility as a safety measure
      arrows.forEach((arrow) => {
         arrow.group.visible = shouldBeVisible;
      });
   }

   /**
    * Create a single arrow as a line with an arrowhead
    */
   function createArrow(position) {
      const arrowGroup = new THREE.Group();

      // Arrow shaft (tube for proper stroke width)
      const shaftCurve = new THREE.LineCurve3(
         new THREE.Vector3(0, 0, 0),
         new THREE.Vector3(0, -config.arrowLength, 0),
      );
      const shaftGeometry = new THREE.TubeGeometry(
         shaftCurve,
         8,
         config.strokeWidth,
         8,
         false,
      );
      const shaftMaterial = new THREE.MeshStandardMaterial({
         color: config.arrowColor,
         emissive: config.arrowColor,
         emissiveIntensity: 0.3,
      });
      const shaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
      arrowGroup.add(shaft);

      // Arrowhead (cone) - rotated 180° to point downward
      const headGeometry = new THREE.ConeGeometry(
         config.arrowheadSize * 0.5,
         config.arrowheadSize,
         8,
      );
      const headMaterial = new THREE.MeshStandardMaterial({
         color: config.arrowColor,
         emissive: config.arrowColor,
         emissiveIntensity: 0.5,
      });
      const head = new THREE.Mesh(headGeometry, headMaterial);
      head.rotation.x = Math.PI; // 180° rotation to point downward
      head.position.y = -config.arrowLength - config.arrowheadSize * 0.5;
      arrowGroup.add(head);

      // Position at world location
      arrowGroup.position.copy(position);
      arrowGroup.position.y += 2; // Start above the surface
      arrowGroup.visible = false; // Start hidden

      return {
         group: arrowGroup,
         shaft,
         head,
         shaftCurve,
         startTime: 0,
         offset: Math.random() * config.loopDuration, // Stagger arrows
      };
   }

   /**
    * Find all WärmeleitNull objects and create arrows at their positions
    */
   function findAndCreateArrows() {
      // Clear existing arrows
      arrows.forEach((arrow) => {
         group.remove(arrow.group);
      });
      arrows.length = 0;

      if (!world?.root) return;

      // Search for WärmeleitNull objects
      world.root.traverse((object) => {
         if (
            object.name?.includes('WärmeleitNull') ||
            object.name?.includes('Wärmeleitung')
         ) {
            const position = object.position.clone();

            // Create multiple arrows at this location if configured
            for (let i = 0; i < config.numArrows; i++) {
               const arrow = createArrow(position);
               arrows.push(arrow);
               group.add(arrow.group);

               // Stagger multiple arrows at same location
               if (config.numArrows > 1) {
                  arrow.offset = (i / config.numArrows) * config.loopDuration;
               }
            }
         }
      });
   }

   /**
    * Update arrow positions and visibility
    */
   function updateArrows(deltaTime) {
      if (!config.enabled || arrows.length === 0) {
         group.visible = config.enabled;
         return;
      }

      group.visible = true;

      arrows.forEach((arrow) => {
         arrow.startTime += deltaTime;

         // Calculate position in loop
         const cycleTime =
            (arrow.startTime + arrow.offset) % config.loopDuration;
         const progress = cycleTime / config.loopDuration;

         // Smooth easing for animation
         const easeProgress = progress;
         const displacement = easeProgress * config.distance;

         arrow.group.position.y = arrow.group.userData.startY - displacement;
      });
   }

   /**
    * Update arrow visuals based on config
    */
   function updateArrowVisuals() {
      arrows.forEach((arrow) => {
         // Update arrowhead
         arrow.head.material.color.setHex(config.arrowColor);
         arrow.head.material.emissive.setHex(config.arrowColor);
         arrow.head.geometry.dispose();
         const newHeadGeometry = new THREE.ConeGeometry(
            config.arrowheadSize * 0.5,
            config.arrowheadSize,
            8,
         );
         arrow.head.geometry = newHeadGeometry;
         arrow.head.rotation.x = Math.PI; // Maintain 180° rotation
         arrow.head.position.y =
            -config.arrowLength - config.arrowheadSize * 0.5;

         // Update shaft
         arrow.shaft.material.color.setHex(config.arrowColor);
         arrow.shaft.material.emissive.setHex(config.arrowColor);
         arrow.shaft.geometry.dispose();
         const newShaftCurve = new THREE.LineCurve3(
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, -config.arrowLength, 0),
         );
         const newShaftGeometry = new THREE.TubeGeometry(
            newShaftCurve,
            8,
            config.strokeWidth,
            8,
            false,
         );
         arrow.shaft.geometry = newShaftGeometry;
         arrow.shaftCurve = newShaftCurve;
      });
   }

   /**
    * Reinitialize arrows and store start positions
    */
   function reinitialize() {
      findAndCreateArrows();
      arrows.forEach((arrow) => {
         arrow.group.userData.startY = arrow.group.position.y;
         arrow.startTime = 0;
         arrow.group.visible = false; // Ensure hidden after reinit
      });
   }

   // Initialize arrows
   reinitialize();

   // Ensure arrows are hidden initially until Wärmeleitfähigkeit state
   updateVisibilityFromState();

   // Create debug UI if enabled
   if (debugUIEnabled) {
      debugUI = createArrowDebugUI(config, {
         onConfigChange: updateArrowVisuals,
         onReinitialize: reinitialize,
      });
   }

   return {
      update(deltaTime) {
         if (!destroyed) {
            // Always check state first - this is the gatekeeper
            updateVisibilityFromState();

            // Only update animation if we should be displaying
            if (group.visible) {
               updateArrows(deltaTime);
            }
         }
      },

      setVisible(visible) {
         config.enabled = visible;
         updateVisibilityFromState();
      },

      getConfig() {
         return { ...config };
      },

      setConfig(newConfig) {
         Object.assign(config, newConfig);
         updateArrowVisuals();
      },

      onStateChanged() {
         updateVisibilityFromState();
      },

      reinitialize,

      destroy() {
         destroyed = true;
         arrows.forEach((arrow) => {
            arrow.shaft.geometry.dispose();
            arrow.shaft.material.dispose();
            arrow.head.geometry.dispose();
            arrow.head.material.dispose();
            group.remove(arrow.group);
         });
         arrows.length = 0;
         rootScene.remove(group);
         if (debugUI?.destroy) {
            debugUI.destroy();
         }
      },
   };
}
